import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import sortBy from 'lodash/sortBy';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import ErrorIllustration from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { AttributesList } from '@atlassian/jira-servicedesk-insight-attribute-list/src/ui/index.tsx';
import type { CmdbReferencedObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import type { CmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type {
	CmdbObject,
	CmdbUiSource,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import type { CmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { createObjectDetailUrl } from '@atlassian/jira-servicedesk-insight-urls/src/index.tsx';
import { TrackObjectView } from '@atlassian/jira-servicedesk-insight-user-history-tracker/src/ui/index.tsx';
import { SidebarHeader } from '../../common/ui/sidebar-header/index.tsx';
import { TitleSkeleton, AttributesListSkeleton } from '../../common/ui/skeletons/index.tsx';
import { LinkedIssues } from './linked-issues/index.tsx';
import messages from './messages.tsx';
import { getPositionFromObjectAttribute } from './utils.tsx';

export type Props = {
	selectedObjectId: CmdbObjectId;
	objectDetails: CmdbObject | undefined;
	objectDetailsError: Error | undefined;
	objectDetailsLoading: boolean;
	source: CmdbUiSource;
	onObjectAttributeClick: (arg1: CmdbReferencedObject) => void;
	onRefreshButtonClick: () => void;
};

export const ObjectView = (props: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		objectDetails,
		objectDetailsError,
		objectDetailsLoading,
		onObjectAttributeClick,
		onRefreshButtonClick,
		selectedObjectId,
		source,
	} = props;

	const memoizedObjectDetails = useMemo(() => {
		if (objectDetailsError) {
			const renderImage = () =>
				getAkEmptyStateRenderImageFn(ErrorIllustration)({
					imageWidth: 120,
					maxImageWidth: 120,
					maxImageHeight: 212,
				});
			return (
				<>
					<EmptyState
						header={formatMessage(messages.errorTitle)}
						description={formatMessage(messages.errorDescription)}
						renderImage={renderImage}
					/>
					<ObjectViewErrorRefreshButtonWrapper>
						<Button appearance="primary" onClick={onRefreshButtonClick}>
							{formatMessage(messages.refreshButton)}
						</Button>
					</ObjectViewErrorRefreshButtonWrapper>
				</>
			);
		}

		if (objectDetailsLoading) {
			return <AttributesListSkeleton />;
		}

		return objectDetails !== undefined ? (
			<>
				<AttributesList
					attributes={sortBy(objectDetails.attributes, (attribute: CmdbObjectAttribute) =>
						getPositionFromObjectAttribute(attribute),
					)}
					objectId={selectedObjectId}
					onObjectAttributeClick={onObjectAttributeClick}
				/>
				<TrackObjectView id={selectedObjectId} />
			</>
		) : null;
	}, [
		selectedObjectId,
		formatMessage,
		objectDetailsError,
		objectDetailsLoading,
		objectDetails,
		onObjectAttributeClick,
		onRefreshButtonClick,
	]);

	const memoizedTabs = useMemo(
		() => [
			{
				label: formatMessage(messages.objectDetails),
				content: memoizedObjectDetails,
				tabItemId: 'insightObjectGraphObjectDetails',
			},
			{
				label: formatMessage(
					fg('itsm-issue-renaming') ? messages.linkedIssuesIssueTermRefresh : messages.linkedIssues,
				),
				content: <LinkedIssues objectId={selectedObjectId} />,
				tabItemId: 'insightObjectGraphLinkedIssues',
			},
		],
		[formatMessage, memoizedObjectDetails, selectedObjectId],
	);

	const onTabSelect = (selectedTab: { tabItemId: string }) => {
		const id: string = selectedTab.tabItemId;
		fireUIAnalytics(createAnalyticsEvent({}), 'navigationItem clicked', id, {
			source,
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (index: any) => {
		const selectedTab = memoizedTabs[index];
		onTabSelect(selectedTab);
	};

	const memoizedHeader = useMemo(() => {
		if (objectDetailsError) {
			return null;
		}

		if (objectDetailsLoading) {
			return <TitleSkeleton />;
		}

		return objectDetails !== undefined ? (
			<SidebarHeader
				title={objectDetails.label}
				iconUrl={objectDetails.avatar.url48}
				link={createObjectDetailUrl(selectedObjectId)}
				tooltip={formatMessage(messages.openObjectDetailsPage)}
			/>
		) : null;
	}, [formatMessage, selectedObjectId, objectDetailsError, objectDetailsLoading, objectDetails]);

	return (
		<Box xcss={wrapperStyles}>
			{memoizedHeader}
			<Tabs
				onChange={handleChange}
				id="object-view-tabs"
				shouldUnmountTabPanelOnChange
				testId="servicedesk-insight-object-graph.ui.object-view.tabs"
			>
				<TabList>
					{memoizedTabs.map((tab) => (
						<Tab key={tab.tabItemId}>{tab.label}</Tab>
					))}
				</TabList>
				{memoizedTabs.map((tab) => (
					<TabPanel key={tab.tabItemId}>
						<TabContentWrapper>{tab.content}</TabContentWrapper>
					</TabPanel>
				))}
			</Tabs>
		</Box>
	);
};

const wrapperStyles = xcss({
	padding: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectViewErrorRefreshButtonWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabContentWrapper = styled.div({
	width: '100%',
	boxSizing: 'border-box',
	marginTop: token('space.200'),
	padding: 0,
});
